import { FC, useState, useEffect } from "react";
import { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import { useSeo } from "../../shared/components/UseSeoComponent";
import CarouselComponent from "../../shared/components/SliderComponent";
import { ROUTES } from "../../routes";
import { currentLanguage } from "../../i18n/type";
import "./style.css";

interface HomePageProps {
  maxWidth: number;
  screenWidth: number;
  screenHeight: number;
  t: TFunction<"translation", undefined>;
}

const HomePage: FC<HomePageProps> = (props: HomePageProps) => {
  const { t, maxWidth, screenWidth, screenHeight } = props;
  const navigate = useNavigate();
  const [experienceCount, setExperienceCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useSeo("homePage");

  useEffect(() => {
    // About section animasyonunu başlat
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isVisible) {
      // Tecrübe sayısını 0'dan 10'a kadar animasyonlu olarak artır
      const interval = setInterval(() => {
        setExperienceCount((prev) => {
          if (prev < 16) {
            return prev + 1;
          }
          clearInterval(interval);
          return prev;
        });
      }, 200);

      return () => clearInterval(interval);
    }
  }, [isVisible]);

  const handleContactClick = () => {
    navigate(`/${currentLanguage}/${ROUTES.contact[currentLanguage]}`);
  };

  // Translation language cards data
  const translationCards = [
    {
      id: 1,
      frontImage: "🇵🇱",
      frontTitle: t("homePage.translationCards.card1.frontTitle"),
      backTitle: t("homePage.translationCards.card1.backTitle"),
      backDescription: t("homePage.translationCards.card1.backDescription"),
      color: "#ff6b35",
      icon: "📄",
    },
    {
      id: 2,
      frontImage: "🇬🇧",
      frontTitle: t("homePage.translationCards.card2.frontTitle"),
      backTitle: t("homePage.translationCards.card2.backTitle"),
      backDescription: t("homePage.translationCards.card2.backDescription"),
      color: "#4ecdc4",
      icon: "⚡",
    },
    {
      id: 3,
      frontImage: "🌍",
      frontTitle: t("homePage.translationCards.card3.frontTitle"),
      backTitle: t("homePage.translationCards.card3.backTitle"),
      backDescription: t("homePage.translationCards.card3.backDescription"),
      color: "#45b7d1",
      icon: "🎯",
    },
  ];

  return (
    <>
      {/* Main Hero Carousel - Full Width */}
      <div className="carousel-section">
        <CarouselComponent
          maxWidth={maxWidth}
          screenWidth={screenWidth}
          screenHeight={screenHeight}
          t={t}
        />
      </div>

      {/* About Section */}
      <div className="about-section">
        <div className="container">
          <div className="about-content">
            {/* Sol Bölüm - Fotoğraf */}
            <div className="about-image-section">
              <div className="profile-image-container">
                <img
                  src="/images/hakkımızda.jpg"
                  alt="Hakkımızda"
                  className="profile-image"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/400x500/f0f0f0/cccccc?text=Hakkımızda";
                  }}
                />
              </div>
            </div>

            {/* Sağ Bölüm - İçerik */}
            <div className="about-text-section">
              <h1 className="about-title">{t("aboutPage.title")}</h1>

              <p className="about-description">{t("aboutPage.description")}</p>

              <div
                className={`experience-section ${isVisible ? "animate" : ""}`}
              >
                <div className="experience-number">{experienceCount}</div>
                <div className="experience-label">
                  {t("aboutPage.experience")}
                </div>
              </div>

              <button className="contact-button" onClick={handleContactClick}>
                {t("aboutPage.contactButton")}
              </button>
            </div>
          </div>

          {/* Alt Bölüm - Ek Metin */}
          <div
            className={`additional-text-section ${isVisible ? "animate" : ""}`}
          >
            <p className="additional-text">{t("aboutPage.additionalText")}</p>
          </div>
        </div>
      </div>

      {/* Translation Cards Section */}
      <div className="translation-cards-section">
        <div className="container">
          <h2 className="section-title text-center mb-5">
            {t("homePage.secondSlider.title")}
          </h2>
          <div className="cards-grid">
            {translationCards.map((card) => (
              <div key={card.id} className="flip-card">
                <div className="flip-card-inner">
                  {/* Front Side */}
                  <div
                    className="flip-card-front"
                    style={{ backgroundColor: card.color }}
                  >
                    <div className="card-header">
                      <div className="language-flag">{card.frontImage}</div>
                      <div className="service-icon">{card.icon}</div>
                    </div>
                    <div className="card-main">
                      <h3 className="card-title">{card.frontTitle}</h3>
                      <div className="card-subtitle">
                        {t("homePage.translationCards.subtitle")}
                      </div>
                    </div>
                  </div>

                  {/* Back Side */}
                  <div
                    className="flip-card-back"
                    style={{ backgroundColor: card.color }}
                  >
                    <div className="card-content">
                      <div className="back-icon">{card.icon}</div>
                      <h3 className="card-title">{card.backTitle}</h3>
                      <p className="card-description">{card.backDescription}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="services-section mt-5">
        <div className="container">
          <h2 className="services-title">{t("servicesPage.title")}</h2>

          <div className="services-content">
            {/* Sözlü Çeviri */}
            <div className="services-column">
              <div className="service-category">
                <h3 className="category-title">
                  {t("servicesPage.oralTranslation.title")}
                </h3>
                <ul className="service-list">
                  {(
                    t("servicesPage.oralTranslation.items", {
                      returnObjects: true,
                    }) as string[]
                  ).map((item: string, index: number) => (
                    <li key={index} className="service-item">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Yazılı Çeviri */}
            <div className="services-column">
              <div className="service-category">
                <h3 className="category-title">
                  {t("servicesPage.writtenTranslation.title")}
                </h3>
                <ul className="service-list">
                  {(
                    t("servicesPage.writtenTranslation.items", {
                      returnObjects: true,
                    }) as string[]
                  ).map((item: string, index: number) => (
                    <li key={index} className="service-item">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* İş Deneyimlerim Section */}
      <div className="experience-section-new">
        <div className="container">
          <h2 className="experience-title">
            {t("homePage.experienceSection.title")}
          </h2>
          <div className="experience-grid">
            <div className="experience-column">
              <div className="experience-text">
                {t("experiencePage.content")
                  .split("\n\n")
                  .map((paragraph: string, index: number) => (
                    <p key={index} className="experience-paragraph">
                      {paragraph}
                    </p>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="contact-section">
        <div className="container">
          <div className="row">
            {/* Sol taraf - Harita */}
            <div className="col-md-6">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1510.4169838529242!2d29.34674544021587!3d40.78766415685524!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cade3db0538d99%3A0xbcdac64f407bbeca!2sBayramo%C4%9Flu%2C%2041700%20Dar%C4%B1ca%2FKocaeli!5e0!3m2!1str!2str!4v1752971672747!5m2!1str!2str"
                title="address"
                allowFullScreen
                loading="lazy"
                className="w-100"
                style={{ height: "400px", border: "none", borderRadius: "8px" }}
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Sağ taraf - İletişim Bilgileri */}
            <div className="col-md-6 d-flex align-items-center justify-content-end">
              <div className="contact-info" style={{ textAlign: "right" }}>
                <p
                  style={{
                    margin: "0.5rem 0",
                    fontSize: "1.1rem",
                    color: "#333",
                  }}
                >
                  <strong>{t("contactPage.phoneLabel")}</strong>
                  <a
                    href="tel:05442859212"
                    style={{
                      color: "#007bff",
                      textDecoration: "none",
                      fontWeight: "500",
                      marginLeft: "0.5rem",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.textDecoration = "underline")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.textDecoration = "none")
                    }
                  >
                    {t("general.phoneNumber")}
                  </a>
                </p>
                <p
                  style={{
                    margin: "0.5rem 0",
                    fontSize: "1.1rem",
                    color: "#333",
                  }}
                >
                  <strong>{t("contactPage.emailLabel")}</strong>
                  <a
                    href="mailto:zuzannabilici@gmail.com"
                    style={{
                      color: "#007bff",
                      textDecoration: "none",
                      fontWeight: "500",
                      marginLeft: "0.5rem",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.textDecoration = "underline")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.textDecoration = "none")
                    }
                  >
                    {t("general.email")}
                  </a>
                </p>
                <p
                  style={{
                    margin: "0.5rem 0",
                    fontSize: "1.1rem",
                    color: "#333",
                  }}
                >
                  <strong>{t("contactPage.address")}:</strong>{" "}
                  {t("general.address")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;
